import {CustomerDetailsService} from 'src/app/angularJS/services/customer-detail/customer-detail.service';
import {CustomerService} from 'src/app/angularJS/services/customer/customer.service';
import {BreadcrumbService} from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import {FulfilmentRequestsService} from 'src/app/angularJS/services/fulfilment/fulfilment.service';
import {QueryBuilder} from 'src/app/angularJS/services/query-builder.service';
import FulfilmentRequest = InterACT.Interfaces.FulfilmentRequests.FulfilmentRequest;

/**
 * A Controller which uses the {@link actora.services.customerService} to handle the retrieval
 * and display of smart media associated with a customer.
 */
export class CustomerSmartMediaListController {

  public static $inject = ['customer', 'QueryBuilder', 'customerDetailService', 'customerService', '$stateParams', '$location', 'breadcrumbService', 'fulfilmentRequestsService', '$state'];
  public cards: Array<InterACT.Interfaces.Cards.Card>;
  public customer: InterACT.Interfaces.Customers.Customer;
  public fulfilmentPaginator: InterACT.Interfaces.IPaginationData = {
    perPageCount: 15,
    currentPage: 1,
    totalCount: 0
  };
  public fulfilmentRequests: Array<FulfilmentRequest> = [];

  constructor(
    customer: InterACT.Interfaces.Customers.Customer,
    private queryBuilder: QueryBuilder,
    private customerDetailService: CustomerDetailsService,
    private customerService: CustomerService,
    private $stateParams: ng.ui.IStateParamsService,
    private $location: ng.ILocationService,
    private breadcrumbService: BreadcrumbService,
    private fulfilmentRequestsService: FulfilmentRequestsService,
    private $state: ng.ui.IStateService
  ) {
    this.customer = angular.copy(customer);
    this.activate();
  }

  /**
   * Generates string representing a cards card number.
   *
   * Will try to get the cards ISRN, failing that the card's
   * serial number, and failing that 'Unknown'.
   */
  public getCardNumber = (card: InterACT.Interfaces.Cards.Card) => {
    let cardNumber = card.cardNumber['ISRN'];

    if (!cardNumber) {
      cardNumber = card.cardNumber['SERIAL_NUMBER'];
    }

    if (!cardNumber) {
      cardNumber = 'Unknown';
    }

    return cardNumber;
  }

  /**
   * Generates a comma separated string listing a card's applications
   */
  public getMediaType = (card: InterACT.Interfaces.Cards.Card) => {
    return card.cardApplications.join(', ');
  }

  /**
   * Fetches card fulfilment requests of type BLOCK, NEW_CARD and REPLACE_CARD if there are any present for the customer or their cards.
   *
   * @param page The new current page
   * @param perPage The new per page count
   */
  public getFulfilmentRequests = (page: number, perPage: number) => {
    if (!perPage) {
      perPage = this.fulfilmentPaginator.perPageCount;
    }

    if (!page) {
      page = this.fulfilmentPaginator.currentPage;
    }
    const fulfilmentQuery = this.buildFulfilmentQuery();

    this.fulfilmentRequestsService.getFulfilmentRequests(page, perPage, fulfilmentQuery).then((fulfilmentResponse) => {
      this.fulfilmentRequests = fulfilmentResponse.fulfilmentRequests;
      this.fulfilmentPaginator.totalCount = fulfilmentResponse.meta.totalCount;
    });
  }


  /**
   * Get Fulfilment's Request State
   *
   */
  public getState(fulfilmentRequest: InterACT.Interfaces.FulfilmentRequests.FulfilmentRequest) {
    return this.fulfilmentRequestsService.getFulfilmentRequestState(fulfilmentRequest);
  }

  /**
   * Changes the state to the Fulfilment Request Summary view
   *
   * @param fulfilmentRequest The fulfilment Request object we wish to view in summary tab
   */
  public goToFulfilmentSummary = (fulfilmentRequest: FulfilmentRequest) => {
    this.$state.go('fulfilment-request.summary', {
      externalId: fulfilmentRequest.externalId
    });
  }

  /**
   * A method which is used to initialize controller and load required data.
   */
  private activate() {
    const customerId = this.$stateParams.customerId;

    if (customerId === 'anonymous') {
      this.breadcrumbService.setBreadcrumbs([
        {
          name: 'SEARCH',
          sref: 'customers'
        }, {
          name: 'CUSTOMER_TABS.ANONYMOUS',
          sref: 'customers',
          options: {
            reload: 'customer'
          }
        }, {
          name: 'CUSTOMER_TABS.SMARTMEDIA',
          sref: 'customer.smartmedia'
        }
      ]);
      if (this.$location.search().hasOwnProperty('cardNumber')) {
        const cardNumber = this.$location.search()['cardNumber'];

        this.customerService.getCards('cardNumber.ISRN=' + cardNumber + ' OR cardNumber.SERIAL_NUMBER=' + cardNumber).then((cards) => {
          this.cards = cards;

          this.getFulfilmentRequests(this.fulfilmentPaginator.currentPage, this.fulfilmentPaginator.perPageCount);
        });
      } else {
        this.cards = [];
        this.fulfilmentRequests = [];
      }
      return;

    } else {

      const enhancedDetails = this.customerDetailService.getEnhancedCustomerData(this.customer);
      const fullName = enhancedDetails.fullName;

      this.breadcrumbService.setBreadcrumbs([
        {
          name: 'SEARCH',
          sref: 'customers'
        }, {
          name: fullName,
          sref: 'customer.details',
          options: {
            reload: 'customer'
          }
        }, {
          name: 'CUSTOMER_TABS.SMARTMEDIA',
          sref: 'customer.smartmedia'
        }
      ]);


      this.customerService.getCards('customer.link=/customers/' + this.$stateParams.customerId)
        .then((cards) => {
          this.cards = cards;

          this.getFulfilmentRequests(this.fulfilmentPaginator.currentPage, this.fulfilmentPaginator.perPageCount);
        });
    }
  }

  private buildFulfilmentQuery() {
    let fulfilmentQuery = this.queryBuilder.newParam()
      .openSubQuery()
      .openSubQuery()
      .fieldIs('customer.link', '/customers/' + this.customer.id)
      .and()
      .openSubQuery()
      .fieldIs('actions.type', 'NEW_CARD')
      .or()
      .fieldIs('actions.type', 'REPLACE_CARD')
      .closeSubQuery()
      .closeSubQuery()
      .getParam();

    // Construct term for cards if they are present.
    if (this.cards && this.cards.length > 0) {
      const listOfIsrns = this.findListOfIsrns(this.cards)
      if (listOfIsrns.length > 0) {
        fulfilmentQuery += ' or ( ('
        fulfilmentQuery += listOfIsrns.join(' or ')
        fulfilmentQuery += ' ) and actions.type=\'BLOCK\' )'
      }
    }

    fulfilmentQuery += ' ) and state.name!=\'FULFILMENT_CANCELLED\' and state.name!=\'FULFILMENT_SUCCESSFUL\' sortBy timestamp desc'
    return fulfilmentQuery;
  }

  private findListOfIsrns(cards: Array<InterACT.Interfaces.Cards.Card>) {
    return cards.filter( card =>
      card.cardNumber && card.cardNumber['ISRN'] != undefined
    ).map(card => 'target.reference=\'' + card.cardNumber['ISRN'] + '\'');
  }
}
